import React, { useState, Fragment, useEffect } from 'react'
import {
  ActionProps,
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { useApi } from '../../hooks/useApi'
import { positionAlertHeaderConfig } from './config'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { getPositionAlerts } from './api'
import { ApiResponse } from '../../typings/ApiResponse'
import { Status } from '../../hooks/useFetchState'
import { useWarehouses } from '../../hooks'
import { usePagination } from '../../hooks/usePagination'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { PositionAlertsDetails } from '../../components/PositionAlerts/PositionAlertDetails/PositionAlertsDetails'
import { ResolveAlertModal } from '../../components/PositionAlerts/Modals/ResolverAlertModal'
import {
  PositionAlert,
  PositionAlertsPageProps
} from '../../interfaces/PositionAlerts/PositionAlerts'
import { useTypesAlert } from '../../hooks/useTypeAlert'
import { useFlows } from '../../hooks/useFlows'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { Filter } from './config/types'
import { defaultFilters, filterOptions } from './config/deafultFilters'
import { PositionAlertHeaderInfo } from './PostitionAlertHeaderInfo'

export const PositionAlertsPage: React.FC<PositionAlertsPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<PositionAlert>>()
  const [selectedFilters, setSelectedFilters] =
    useState<Filter[]>(defaultFilters)

  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const { closeModal, isOpen, openModal, size, title, extendedProps } =
    useModal<PositionAlert>('Detalles de alertas de posición')

  const resolveModal = useModal<{ positionAlertIds: number[] }>(
    'Resolver Alerta'
  )

  const requestPickingProblems = (
    page?: number,
    pageSize?: number,
    filters?: Filter[]
  ) => {
    callApi(getPositionAlerts(orderBy, page, pageSize, filters || []))
  }

  const {
    flowsState: { flows },
    getFlows
  } = useFlows()

  const {
    typesAlertState: { typesAlert },
    getTypesAlert
  } = useTypesAlert()

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  useEffect(() => {
    if (typesAlert?.length === 0) {
      getTypesAlert()
    }
  }, [])

  useEffect(() => {
    if (flows?.length === 0) {
      getFlows()
    }
  }, [])

  const positionAlertTableActions: ActionProps<PositionAlert> = {
    onView: (_id: number | string, row: PositionAlert) => openModal('xl', row)
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestPickingProblems(auxPage, pageSize, selectedFilters)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleCloseModalMessage = (success: boolean, message: string) => {
    openSnackBar(message, success)
    if (success) {
      resolveModal.closeModal()
      requestPickingProblems(0, pagination.pageSize, selectedFilters)
    }
  }

  const handleCloseModalDetails = () => {
    closeModal()
    requestPickingProblems(
      pagination.page,
      pagination.pageSize,
      selectedFilters
    )
  }

  const [orderBy, setOrderBy] = useState<GenericOrderBy<PositionAlert>>({
    field: 'positionName',
    direction: 'asc'
  })
  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<PositionAlert>) => {
    if (field === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((prev) => ({ ...prev, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  // en caso que usen el orden debe mantener la pagina
  useEffect(() => {
    requestPickingProblems(
      pagination.page,
      pagination.pageSize,
      selectedFilters
    )
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          onSearchClick={(f) => {
            pagination.setPagination(0, pagination.pageSize)
            requestPickingProblems(0, pagination.pageSize, f)
          }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeAddButton={true}
          includeSearchOnClean={true}
        />
        <PositionAlertHeaderInfo />
        <GenericTable<PositionAlert>
          IDKey='positionAlertId'
          headersConfig={positionAlertHeaderConfig}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          actions={positionAlertTableActions}
          isLoading={state.status === Status.Loading}
          onSortChange={handleSorting}
          orderField={orderBy}
        />
      </StandardPage>

      <Modal
        open={isOpen}
        onClose={handleCloseModalDetails}
        maxWidth={size}
        title={title}
      >
        {extendedProps && (
          <PositionAlertsDetails positionAlert={extendedProps} />
        )}
      </Modal>

      <ResolveAlertModal
        {...resolveModal}
        handleCloseModalMessage={handleCloseModalMessage}
      />

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
